import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight, Phone, EnvelopeSimple, WhatsappLogo, MapPin, Clock, Globe } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const TITLES = {
    philosophy: "企業理念",
    greeting: "代表挨拶",
    news: "お知らせ",
    about: "運営会社について",
    contact: "お問い合わせ",
    careers: "採用情報",
    members: "会員募集",
    privacy: "個人情報の取扱いについて",
    tokushoho: "特定商取引法に基づく表記",
};

/**
 * Modal overlay that shows info content (philosophy/greeting/news/about/...) inline,
 * without page navigation. Content sourced from /api/settings/public.
 */
export default function InfoModal({ slug, onClose }) {
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!slug) return;
        api.get("/settings/public").then((r) => setData(r.data)).catch(() => setData({}));
    }, [slug]);

    useEffect(() => {
        if (!slug) return;
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [slug, onClose]);

    if (!slug) return null;
    const title = TITLES[slug] || "情報";
    const cms = data?.cms || {};
    const op = data?.operator || {};

    let body = null;
    if (slug === "philosophy") body = <Paragraph text={cms.philosophy} />;
    else if (slug === "greeting") body = <Paragraph text={cms.greeting} />;
    else if (slug === "news") body = <NewsList items={cms.news || []} />;
    else if (slug === "about") body = <AboutCompany op={op} />;
    else if (slug === "contact") body = <ContactInfo op={op} />;
    else if (slug === "careers") body = <Paragraph text={cms.careers} footer={op.support_email} />;
    else if (slug === "members") body = <Paragraph text={cms.members} cta={{ to: "/register", label: "新規登録へ", onCta: onClose }} />;
    else if (slug === "privacy") body = <Paragraph text={cms.privacy_policy} />;
    else if (slug === "tokushoho") body = <TokushohoView cms={cms} op={op} />;
    else body = <p className="text-[var(--prc-muted)]">コンテンツが見つかりません。</p>;

    return (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-4" data-testid={`info-modal-${slug}`} onClick={onClose}>
            <div
                className="bg-[var(--prc-modal-bg)] border border-[var(--prc-border-strong)] rounded-sm w-full max-w-3xl max-h-[90vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between border-b border-[var(--prc-border)] px-5 py-4 sticky top-0 bg-[var(--prc-modal-bg)] z-10">
                    <div className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-[var(--prc-green)]" style={{ boxShadow: "0 0 8px var(--prc-green)" }} />
                        <p className="prc-label-overline !text-[var(--prc-green)]">// {title.toUpperCase()}</p>
                    </div>
                    <button onClick={onClose} className="prc-btn-ghost !px-2" data-testid={`info-modal-close-${slug}`} aria-label="close">
                        <X size={16} />
                    </button>
                </div>
                <div className="p-6">
                    <h2 className="text-[1.5rem] font-bold tracking-tight text-[var(--prc-ink-2)] mb-5">{title}</h2>
                    {data === null ? (
                        <p className="text-sm text-[var(--prc-muted)]">読み込み中…</p>
                    ) : body}
                </div>
            </div>
        </div>
    );
}

function Paragraph({ text, footer, cta }) {
    return (
        <div className="leading-relaxed">
            <p className="text-[var(--prc-ink-2)] whitespace-pre-wrap">{text || "（内容未設定）"}</p>
            {footer && <p className="mt-4 text-sm text-[var(--prc-muted)]">お問い合わせ: <a className="text-[var(--prc-green)] hover:underline" href={`mailto:${footer}`}>{footer}</a></p>}
            {cta && (
                <Link to={cta.to} onClick={cta.onCta} className="prc-btn-primary inline-flex items-center gap-2 mt-6">
                    {cta.label} <ArrowRight size={16} />
                </Link>
            )}
        </div>
    );
}

function NewsList({ items }) {
    if (!items.length) return <p className="text-[var(--prc-muted)]">お知らせはまだありません。</p>;
    return (
        <ul className="space-y-2">
            {items.map((n, i) => (
                <li key={i} className="flex gap-4 border border-[var(--prc-border)] bg-[var(--prc-surface-2)] p-3 rounded-sm">
                    <span className="text-xs font-mono text-[var(--prc-muted)] whitespace-nowrap">{n.date}</span>
                    <span className="text-[var(--prc-ink-2)]">{n.title}</span>
                </li>
            ))}
        </ul>
    );
}

function AboutCompany({ op }) {
    return (
        <div className="space-y-3">
            <Row label="商号" value={op.legal_name} />
            <Row label="代表者" value={op.representative} />
            <Row label="所在地" value={op.address} />
            <Row label="電話" value={op.tel} />
            <Row label="FAX" value={op.fax} />
            <Row label="メール" value={op.support_email} />
            <Row label="Web" value={op.website} isLink />
            <Row label="T番号" value={op.tnumber} />
            <Row label="営業時間" value={op.business_hours} />
        </div>
    );
}

function ContactInfo({ op }) {
    return (
        <div className="grid gap-4 md:grid-cols-2">
            {op.tel && <ContactCard Icon={Phone} label="電話" value={op.tel} href={`tel:${op.tel}`} />}
            {op.support_email && <ContactCard Icon={EnvelopeSimple} label="メール" value={op.support_email} href={`mailto:${op.support_email}`} />}
            {op.whatsapp && <ContactCard Icon={WhatsappLogo} label="WhatsApp" value={op.whatsapp} href={`https://wa.me/${op.whatsapp.replace(/[^0-9]/g,'')}`} external />}
            {op.line_id && <ContactCard Icon={Globe} label="LINE ID" value={op.line_id} />}
            {op.business_hours && <ContactCard Icon={Clock} label="営業時間" value={op.business_hours} />}
            {op.address && <ContactCard Icon={MapPin} label="所在地" value={op.address} />}
        </div>
    );
}

function ContactCard({ Icon, label, value, href, external }) {
    const content = (
        <div className="border border-[var(--prc-border)] bg-[var(--prc-surface-2)] rounded-sm p-4 hover:border-[var(--prc-green)] transition">
            <div className="flex items-center gap-2 mb-2">
                <Icon size={16} className="text-[var(--prc-green)]" weight="fill" />
                <p className="prc-label-overline">{label}</p>
            </div>
            <p className="text-[var(--prc-ink-2)] break-all">{value}</p>
        </div>
    );
    if (href) return <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined}>{content}</a>;
    return <div>{content}</div>;
}

function TokushohoView({ cms, op }) {
    return (
        <div className="space-y-3 text-sm">
            <Row label="販売業者" value={op.legal_name} />
            <Row label="代表責任者" value={op.representative} />
            <Row label="所在地" value={op.address} />
            <Row label="電話" value={op.tel} />
            <Row label="メール" value={op.support_email} />
            {cms.tokushoho && <p className="mt-2 text-[var(--prc-ink-2)] whitespace-pre-wrap pt-3 border-t border-[var(--prc-border)]">{cms.tokushoho}</p>}
        </div>
    );
}

function Row({ label, value, isLink }) {
    return (
        <div className="grid grid-cols-[120px_1fr] gap-3 text-sm">
            <span className="text-[var(--prc-muted)] font-mono text-xs uppercase tracking-wider">{label}</span>
            {isLink && value ? (
                <a href={value} target="_blank" rel="noreferrer" className="text-[var(--prc-green)] hover:underline break-all">{value}</a>
            ) : (
                <span className="text-[var(--prc-ink-2)] whitespace-pre-wrap">{value || "—"}</span>
            )}
        </div>
    );
}
